import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Box,
  Container,
  CssBaseline, 
  Drawer,
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import Header from '../common/Header';
import Footer from '../common/Footer';
import {
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  BarChart as BarChartIcon,
  Shield as ShieldIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import { hideNotification } from '../../store/slices/uiSlice';
import { canAccess, PermissionKey } from '../../utils/permissions';

const drawerWidth = 240;
const miniDrawerWidth = 64;

const AdminLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [desktopDrawerOpen, setDesktopDrawerOpen] = useState(!isMobile);
  const { notification } = useSelector((state: RootState) => state.ui);
  const { currentUser } = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const handleNotificationClose = () => {
    dispatch(hideNotification());
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    setDesktopDrawerOpen(!desktopDrawerOpen);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const allMenuItems: { text: string; icon: React.ReactNode; path: string; permission: PermissionKey }[] = [
    { text: 'ユーザー情報', icon: <PeopleIcon />, path: '/admin/users', permission: 'userMgmt.view.canView' },
    { text: 'ロープレ設定', icon: <AssignmentIcon />, path: '/admin/scenarios', permission: 'scenarioMgmt.view.canView' },
    { text: '利用分析', icon: <BarChartIcon />, path: '/admin/analytics', permission: 'analytics.view.canView' },
    { text: '権限プロファイル', icon: <ShieldIcon />, path: '/admin/profiles', permission: 'profileMgmt.view.canView' },
  ];
  const menuItems = allMenuItems.filter((item) => canAccess(currentUser, item.permission));
  const drawer = (isDesktop: boolean = false) => (
    <div>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <Tooltip title={isDesktop && !desktopDrawerOpen ? item.text : ''} placement="right">
              <ListItemButton 
                onClick={() => handleNavigate(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  minHeight: 48,
                  justifyContent: isDesktop && !desktopDrawerOpen ? 'center' : 'initial',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isDesktop && !desktopDrawerOpen ? 'auto' : 3,
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  sx={{ 
                    opacity: isDesktop && !desktopDrawerOpen ? 0 : 1,
                    display: isDesktop && !desktopDrawerOpen ? 'none' : 'block',
                  }} 
                />
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
      {isDesktop && (
        <>
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
            <IconButton onClick={handleDesktopDrawerToggle}>
              {desktopDrawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Box>
        </>
      )}
    </div>
  );

  // パスに基づいてツールバータイトルを設定
  const getToolTitle = () => {
    const path = location.pathname;

    // pathがmenuItemsのpathと一致する場合は、メニューアイテムのテキストを返す
    const matchedItem = menuItems.find(item => path.startsWith(item.path));
    if (matchedItem) {
      return matchedItem.text;
    }
    return '管理者メニュー';
  };

  const toolTitle = getToolTitle();

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      bgcolor: theme.palette.background.paper,
      justifyContent: 'space-between',
    }}>
      <CssBaseline />
      <Header toolTitle={toolTitle} />
      {/* メインコンテンツエリア */}
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          mt: '120px', // 2つのツールバーの高さ分のマージンを追加
        }}
      >
        {/* ドロワーメニュー */}
        <Box
          component="nav"
          sx={{ 
            width: desktopDrawerOpen ? drawerWidth : miniDrawerWidth,
            flexShrink: 0,
            position: 'sticky',
            top: '120px', // ヘッダーの高さと同じ
            height: `calc(100vh - 120px)`, // ビューポートの高さからヘッダーの高さを引く
            overflowY: 'auto',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
          aria-label="admin menu"
        >
          <Drawer
            variant="permanent"
            sx={{
              display: 'block',
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: desktopDrawerOpen ? drawerWidth : miniDrawerWidth,
                position: 'relative',
                height: '100%',
                borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                transition: theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
                overflowX: 'hidden',
              },
            }}
            open
          >
            {drawer(true)}
          </Drawer>
        </Box>

        {/* メインコンテンツ */}
        <Container maxWidth={false}>
          <Box
            component="main"
            sx={{ 
              flexGrow: 1, 
              p: 3, 
              width: '100%',
            }}
          >
            <Outlet />
          </Box>
        </Container>
      </Box>
      <Snackbar 
        open={notification.show} 
        autoHideDuration={6000} 
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleNotificationClose} 
          severity={notification.type} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      <Footer />
    </Box>
  );
};

export default AdminLayout;
